import {useParams} from '@/router/router';
import {getUserId} from '@/services/auth';
import {IDomainCategoryDescription, ProblmSubmissionRoute} from '@/store/state/global.types';
import {mapFilterToFilterTag} from '@/store/api/download-domains';
import {restoreSolutionsBySessionId} from '@/store/api/get-saved-solutions';
import {getSelectedTrips} from '@/store/api/get-selected-trips';
import {
  getAmexSolution,
  getSolutionContext,
  getTripId,
  loadAvailableDomains,
  loadRecommendationDetails,
  loadTripOptionsByProblem,
  restoreTrip,
  saveSolution,
  selectTheme,
  setAvailableTripOptions,
  setMissingFilters,
  setTripPrice,
  updateDestName,
  updateSolutionDates,
  updateSolutionFilters,
  updateSolutionImg,
  updateTripContext,
} from '@/store/state/helpers';
import {TravelersIndormation} from '@/store/api/submit-amex-problem';
import {Price} from '@mobi-components/global-solution';
import {loadSolution} from '../old-home-page/load-solution';

const makeFilters = async (
  filters: IDomainCategoryDescription[],
  context: string,
  travelersFilter?: TravelersIndormation,
  budget: Price[] = [],
) => {
  const activeFilters = filters.map(mapFilterToFilterTag);
  const activeFiltersIds = new Set(activeFilters.map((x) => x.id));
  const allFilters = await loadAvailableDomains(context);
  const allFiltersIds = new Set(allFilters.map((x) => x.id));
  const missingFilters = activeFilters.filter((x) => !allFiltersIds.has(x.id));
  await setMissingFilters(missingFilters, travelersFilter, budget);
  return allFilters.filter((x) => activeFiltersIds.has(x.id)).map((x) => ({...x, value: true}));
};

export async function restoreTripFromTripSample(isPdf = false) {
  if (isPdf) {
    return;
  }
  const solution = getAmexSolution();
  const {tripId} = useParams().value;
  const userId = getUserId();
  const data = await getSelectedTrips(tripId, userId);
  const trip = data?.candidate_trips.find((x) => x.trip_id === tripId);

  if (!trip || !data || solution) {
    return;
  }

  const travelersInformation: TravelersIndormation = data.traveler_information;
  const budget: Price[] = data.budget?.price || [];

  const img = trip.context.images.medium;
  await updateSolutionImg(img);
  selectTheme(trip.theme || 'Custom');
  updateDestName(trip.context.name.replace(/_/g, ', '));
  await setTripPrice(trip.price);
  await updateTripContext(trip.context.name);

  await setAvailableTripOptions(data?.candidate_trips || []);
  const accommodations = trip.accommodations.map((x) => ({id: x.id, name: x.name}));
  const poiIds = trip.activities.concat(accommodations);
  const selectedFilters = await makeFilters(
    data?.selected_filters || [],
    trip.context.name,
    travelersInformation,
    budget,
  );
  await updateSolutionFilters(selectedFilters);

  await loadSolution(tripId, poiIds, ProblmSubmissionRoute.SOLUTION, trip.flights);
  await loadRecommendationDetails(ProblmSubmissionRoute.SOLUTION, trip.context.name);
}

export async function restoreTripFromMyTrips(isPdf = false) {
  const solution = getAmexSolution();
  const params = useParams();
  const sessionId = params.value.tripId;
  const contextName = params.value.contextName;
  const userId = params.value.userId || getUserId();
  const data = (await restoreSolutionsBySessionId(sessionId, userId)) || [];
  const first = data.find(
    (x) => x.trip_id === sessionId || x.problem?.id === sessionId || x.solution_response?.session_id === sessionId,
  );
  if (solution && first) {
    const solutionFilters = await makeFilters(
      first?.selected_filters || [],
      first.solution_response.destination?.name || getSolutionContext(),
      // @ts-ignore
      first.problem.traveler_information,
      first.problem.budget?.price || [],
    );
    updateSolutionFilters(solutionFilters);
  }
  if (!first || solution) {
    return;
  }

  const problem = first.problem;
  if (problem) {
    selectTheme('Custom');
    const currentContext = first.trip_contexts.find((x) => x.name === params.value.contextName);
    const img = currentContext?.images.medium || first.solution_response.destination?.photo || '';
    const name = contextName || first.solution_response.destination?.name || '';
    await updateSolutionImg(img);
    await updateDestName(name.replace(/_/g, ', '));
    updateSolutionDates(first.trip_start_time * 1000, first.trip_end_time * 1000);

    await updateTripContext(name);
    const newTripId = getTripId();
    if (isPdf) {
      await saveSolution(newTripId, first.solution_response);
      const solutionFilters = await makeFilters(
        first.selected_filters,
        first.solution_response.destination?.name || '',
        // @ts-ignore
        first.problem.traveler_information,
      );
      updateSolutionFilters(solutionFilters);
    } else {
      await Promise.all([
        restoreTrip(problem, first.trip_name, first.trip_id), // todo we probably already have a solution, no need to download it once again
        loadTripOptionsByProblem(problem, newTripId).then((trips) => {
          setAvailableTripOptions([...trips.trips]);
        }),
      ]);
      const solutionFilters = await makeFilters(
        first.selected_filters,
        first.solution_response.destination?.name || '',
        // @ts-ignore
        problem.traveler_information,
        problem.budget?.price || [],
      );
      updateSolutionFilters(solutionFilters);
      // await loadRecommendationDetails(ProblmSubmissionRoute.SOLUTION, contextName);
    }
  }
}
